'use client';
import {
  privateQueryContext,
  privateQueryOptions,
} from '@eliadmin/core/src/utils/private-query-context';
import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';
import { useQuery } from '@tanstack/react-query';
import { checkOrderStatus } from '@eliadmin/core/src/api/cart/cart-api';
import { fetchOrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import { OrderConfirmConversionPurchase } from '@eliadmin/core/src/components/analytics/order-confirm-conversion-purchase';
import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import Link from '@eliadmin/core/src/components/link';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import { useRouter } from 'next/navigation';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { HeurekaOrderConversion } from '@eliadmin/core/src/components/heureka/heureka-order-conversion';

export default function OrderDetails({ hash, locale }: any) {
  const { t } = useTranslations();
  const me = useCustomerStore(state => state.me);
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const router = useRouter();

  const { data: order, isError: isOrderError } = useQuery({
    queryKey: ['order', ...privateQueryContext(locale, hash, me?.authToken, locale)],
    ...privateQueryOptions,
    queryFn: () => checkOrderStatus({ locale, token: hash, authToken: me?.authToken }),
  });

  const { data: currencies } = useQuery({
    queryKey: ['currencies', locale],
    queryFn: () => fetchCurrencies({ locale }),
  });

  const { data: orderInfo } = useQuery({
    queryKey: [
      'orderInfo',
      ...privateQueryContext(locale, locale, order?.data?.identifier, order?.data?.email),
    ],
    ...privateQueryOptions,
    queryFn: () =>
      fetchOrderInfo({
        locale,
        identifier: order?.data?.identifier || '',
        email: order?.data?.email,
      }),
    enabled: !!order?.data?.identifier && !!order?.data?.email,
  });

  if (isOrderError) {
    router.push(ROUTES.HOME());
    return null;
  }

  const currencyISO = currencies?.data.find(
    currency => currency.symbol === generalLayout?.currency,
  )?.id;

  return (
    <>
      {generalLayout?.marketing?.heureka_conversion_measurement_id && orderInfo?.data && (
        <HeurekaOrderConversion
          conversionMeasurementKey={generalLayout.marketing.heureka_conversion_measurement_id}
          orderInfo={orderInfo.data}
          currency={currencyISO || generalLayout.currency || ''}
          locale={locale}
        />
      )}
      {orderInfo && (
        <OrderConfirmConversionPurchase
          orderInfo={orderInfo}
          currencyISO={currencyISO || ''}
          generalLayout={{ data: generalLayout } as GeneralLayout}
        />
      )}
      {!order?.data && (
        <div className="flex h-full min-h-[400px] flex-col items-center justify-center space-y-6 py-10">
          <Spinner className="h-12 w-12 text-primary" />
        </div>
      )}
      {order?.data && (
        <div className="flex h-full flex-col items-center justify-center space-y-6 py-10">
          <h1 className="text-center text-4xl font-bold">{t('text352')}</h1>
          <p className="max-w-lg text-center text-lg font-normal">
            {t('text353')} <strong>{order?.data?.identifier}</strong> {t('text354')}{' '}
            <strong>{order?.data?.email}</strong> {t('text355')}
          </p>
          <OrderLoyaltyReward
            reward={orderInfo?.data?.loyalty_reward}
            className="w-full max-w-lg"
          />
          <Button asChild className="w-full md:w-auto">
            <Link href={ROUTES.HOME() ?? '#'} prefetch={false}>
              {t('text160')}
            </Link>
          </Button>
        </div>
      )}
    </>
  );
}
